import clsx from 'clsx'
import type {
  ComponentPropsWithRef,
  HTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes
} from 'react'

/**
 * Shared primitives.
 *
 * Still deliberately small. The design carries its personality in typography, in the frosted
 * surfaces and in the trace lane — not in a component library — and every extra variant here
 * is a chance for two screens to disagree about what a "secondary" button looks like.
 *
 * What changed with the portal design is the *shape language*. Controls are pills, panels are
 * frosted cards with a single hairline edge, and one iridescent gradient is rationed across
 * the whole product: the primary action on a screen, the assistant's own mark, and the user's
 * own message. If a third thing starts wearing it, it stops meaning anything.
 *
 * Colours come from the semantic tokens in `styles/tokens.css` via the Tailwind theme bridge
 * (`bg-card-solid`, `text-fg-muted`, `border-rule`). No component hardcodes a hex — that is
 * what lets one environment variable re-colour the entire app.
 */

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'mono'
type ButtonSize = 'sm' | 'md' | 'lg'

/*
 * `ComponentPropsWithRef` rather than `ButtonHTMLAttributes`, so callers can hold the element.
 *
 * React 19 passes `ref` to a function component as an ordinary prop, so it arrives in `rest`
 * and lands on the `<button>` with everything else — no `forwardRef`, no wrapper. The type is
 * the only thing that has to say so. It exists for the one contract a disclosure has: a form
 * that replaces its own trigger must hand focus back to that trigger when it closes, or a
 * keyboard user is returned to the top of the document.
 */
type ButtonProps = ComponentPropsWithRef<'button'> & {
  variant?: ButtonVariant
  size?: ButtonSize
  icon?: ReactNode
  /** Rendered after the label — the circular arrow chip the primary actions carry. */
  trailing?: ReactNode
}

/**
 * `type="button"` is declared BEFORE `{...rest}` and must stay there.
 *
 * The login form passes `type="submit"` and depends on rest overriding the default. Move the
 * spread above it and the form silently stops submitting — no error, no warning, and every
 * browser test fails at sign-in with a timeout that points nowhere near this file.
 */
export const Button = ({
  variant = 'secondary',
  size = 'md',
  icon,
  trailing,
  className,
  children,
  ...rest
}: ButtonProps) => (
  <button
    type="button"
    className={clsx(
      'inline-flex shrink-0 select-none items-center justify-center gap-2 rounded-full',
      'transition-[transform,box-shadow,background-color,color,border-color] duration-200 ease-out',
      'disabled:pointer-events-none disabled:opacity-45',
      size === 'sm' && 'h-[30px] px-3 text-[12.5px]',
      size === 'md' && 'h-[34px] px-4 text-[13px]',
      size === 'lg' && 'h-[38px] px-5 text-[14px]',
      // The label is set at 600 with tight tracking everywhere except the mono variant, which
      // is a different register entirely: a quiet, technical control rather than an action.
      variant === 'mono' ? 'font-mono text-[10px] tracking-[0.05em]' : 'font-semibold tracking-[-0.014em]',
      variant === 'primary' && [
        'border-0 bg-[image:var(--irid)] text-white shadow-[var(--irid-glow)]',
        'hover:-translate-y-px hover:shadow-[var(--irid-glow-strong)]',
        // The chip sits flush with the right edge, so a primary button carrying one needs
        // less padding on that side than one that does not.
        trailing && (size === 'lg' ? 'pr-[5px]' : 'pr-1')
      ],
      variant === 'secondary' &&
        'border border-rule-strong bg-card-solid text-fg hover:-translate-y-px hover:shadow-glass',
      variant === 'ghost' && 'border border-transparent text-fg-muted hover:bg-card hover:text-fg',
      // `--on-alert` rather than white: the dark theme re-picks --alert light, and white on a
      // pale salmon fill is the one filled control the design does not specify and nobody can
      // read.
      variant === 'danger' && 'border-0 bg-alert text-[var(--on-alert)] hover:-translate-y-px hover:opacity-92',
      variant === 'mono' && 'border border-rule bg-transparent text-fg-muted hover:border-rule-strong hover:text-fg',
      className
    )}
    {...rest}
  >
    {icon}
    {children}
    {trailing}
  </button>
)

/**
 * The circular affordance inside a primary button.
 *
 * A translucent white disc holding an arrow — it reads as a physical key on the button rather
 * than a second icon beside the label, which is what stops "Ask →" from looking like a link.
 */
export const ButtonChip = ({ children, size = 24 }: { children: ReactNode; size?: number }) => (
  <span
    aria-hidden
    className="flex items-center justify-center rounded-full bg-white/20"
    style={{ width: size, height: size }}
  >
    {children}
  </span>
)

type IconButtonProps = ComponentPropsWithRef<'button'> & {
  /** Required: these buttons have no visible text, so this is their only name. */
  label: string
  size?: number
  tone?: 'default' | 'grounded' | 'alert'
  /**
   * Swallow the click before it reaches an ancestor.
   *
   * Off by default. It exists for the row actions in the sidebar, which sit *inside* the
   * `<li>` that wraps a NavLink: without it, pressing Delete navigates to the conversation
   * being deleted before the mutation resolves.
   */
  stopPropagation?: boolean
}

export const IconButton = ({
  label,
  size = 29,
  tone = 'default',
  stopPropagation = false,
  className,
  onClick,
  children,
  ...rest
}: IconButtonProps) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    onClick={(event) => {
      if (stopPropagation) {
        event.preventDefault()
        event.stopPropagation()
      }
      onClick?.(event)
    }}
    style={{ width: size, height: size }}
    className={clsx(
      'inline-flex shrink-0 items-center justify-center rounded-full border transition-colors duration-200',
      'disabled:pointer-events-none disabled:opacity-45',
      tone === 'default' && 'border-rule text-fg-muted hover:border-rule-strong hover:bg-card-solid hover:text-fg',
      tone === 'grounded' &&
        'border-rule text-fg-muted hover:border-grounded hover:bg-grounded-soft hover:text-grounded',
      tone === 'alert' && 'border-rule text-fg-muted hover:border-alert hover:bg-alert-soft hover:text-alert',
      className
    )}
    {...rest}
  >
    {children}
  </button>
)

const fieldClass =
  'w-full rounded-tile border border-rule-strong bg-card-solid text-fg placeholder:text-fg-subtle focus:border-primary focus:outline-none'

export const Input = ({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) => (
  <input className={clsx(fieldClass, 'h-[34px] px-3 text-[13px]', className)} {...rest} />
)

export const Textarea = ({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) => (
  <textarea className={clsx(fieldClass, 'resize-none px-3 py-2 text-[13px] leading-relaxed', className)} {...rest} />
)

/**
 * A native `<select>`, restyled.
 *
 * Native rather than a custom listbox on purpose: the platform menu is keyboard-complete, it
 * is the only version that works properly on a phone, and nothing here needs a control rich
 * enough to justify re-implementing all of that.
 */
export const Select = ({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) => (
  <select className={clsx(fieldClass, 'h-[34px] cursor-pointer pl-3 pr-7 text-[13px]', className)} {...rest}>
    {children}
  </select>
)

/**
 * The shared shape of a pickable row: a checkbox or radio, its label, and its consequence.
 *
 * Exported because two planes draw the same row. It began in the builder and moved here when
 * the knowledge console needed the radio below — a maintainer choosing who may use a base and
 * a maker choosing who may use an agent are the same decision, and they should not look like
 * two different products.
 */
export const pickableClass = (picked: boolean, disabled?: boolean) =>
  clsx(
    'flex items-start gap-2.5 rounded-tile border px-3 py-2.5',
    'transition-[background-color,border-color,box-shadow] duration-200',
    picked ? 'border-rule-strong bg-card-solid shadow-glass' : 'border-rule bg-card-solid/55 hover:border-rule-strong',
    disabled ? 'cursor-not-allowed opacity-45' : 'cursor-pointer'
  )

/** A radio row where each option carries its consequence, used for audience and copies. */
export const ChoiceRow = <T extends string>({
  name,
  value,
  options,
  disabled,
  onChange
}: {
  name: string
  value: T
  options: Array<{ value: T; label: string; hint: string }>
  disabled?: boolean
  onChange: (next: T) => void
}) => (
  <div className="space-y-2">
    {options.map((option) => (
      <label key={option.value} className={pickableClass(option.value === value, disabled)}>
        <input
          type="radio"
          name={name}
          value={option.value}
          checked={option.value === value}
          disabled={disabled}
          onChange={() => onChange(option.value)}
          className="mt-[3px] h-[15px] w-[15px] shrink-0 cursor-pointer accent-primary"
        />
        <span className="min-w-0">
          <span className="block text-[13.5px] font-semibold tracking-[-0.014em]">{option.label}</span>
          <span className="mt-1 block text-pretty text-[12.5px] leading-[1.5] text-fg-muted">{option.hint}</span>
        </span>
      </label>
    ))}
  </div>
)

type PanelProps = HTMLAttributes<HTMLDivElement> & {
  /**
   * Opaque instead of translucent. Use it when something sits *on* a panel — a nested card
   * over a frosted surface is two layers of the same wash and reads as fog.
   */
  solid?: boolean
}

export const Panel = ({ solid = false, className, children, ...rest }: PanelProps) => (
  <div
    className={clsx('rounded-card border border-rule shadow-glass', solid ? 'bg-card-solid' : 'bg-card', className)}
    {...rest}
  >
    {children}
  </div>
)

type BadgeTone = 'neutral' | 'primary' | 'grounded' | 'alert'

/**
 * The status pill.
 *
 * Mono, small caps, generously tracked — the same register as the eyebrow labels, so a badge
 * reads as a machine-set fact rather than as prose. Tone never travels alone: every call site
 * pairs it with an icon or a word, because colour is not allowed to be the only carrier of
 * meaning (AGM-124).
 */
export const Badge = ({
  tone = 'neutral',
  children,
  className
}: {
  tone?: BadgeTone
  children: ReactNode
  className?: string
}) => (
  <span
    className={clsx(
      'inline-flex items-center gap-1 rounded-full px-2 py-[2.5px]',
      'font-mono text-[9px] uppercase leading-[1.35] tracking-[0.09em] tabular-nums',
      tone === 'neutral' && 'border border-rule bg-card text-fg-muted',
      tone === 'primary' && 'bg-primary-soft text-primary',
      tone === 'grounded' && 'bg-grounded-soft text-grounded',
      tone === 'alert' && 'bg-alert-soft text-alert',
      className
    )}
  >
    {children}
  </span>
)

/** A labelled measured value. The workhorse of the instrument aesthetic. */
export const Readout = ({ label, value, tone }: { label: string; value: ReactNode; tone?: 'primary' | 'alert' }) => (
  <span className="inline-flex items-baseline gap-1.5">
    <span className="eyebrow">{label}</span>
    <span
      className={clsx('readout font-medium', tone === 'primary' && 'text-primary', tone === 'alert' && 'text-alert')}
    >
      {value}
    </span>
  </span>
)

/**
 * A capacity gauge: how full something is, in words, with a bar beside them.
 *
 * The one instrument shared between the operator's storage screen and a maker's knowledge base,
 * which is why it lives here rather than in either. Two rules are load-bearing:
 *
 *   - **The words are the fact and the bar is decoration.** The track is `aria-hidden` and the
 *     reading is plain text, following the same pattern as every sparkline in this product. A
 *     `role="meter"` would promise a value/min/max relationship that assistive technology
 *     announces inconsistently, for a fact that is already a sentence.
 *   - **No limit, no bar.** `fill: null` renders the figure alone. Most stores in this product
 *     have no ceiling, and a full-width bar against an imagined one is the most confident kind
 *     of wrong.
 *
 * Tone is never the only carrier: an `alert` meter is paired by its call sites with a word or an
 * icon, because a bar that is merely a different colour says nothing in monochrome (AGM-124).
 */
export const Meter = ({
  label,
  value,
  detail,
  fill,
  tone = 'primary',
  className
}: {
  label: string
  value: ReactNode
  detail?: ReactNode
  /** 0…1, or null when there is no limit for this to be a fraction of. */
  fill: number | null
  tone?: 'primary' | 'grounded' | 'alert'
  className?: string
}) => (
  <div className={className}>
    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
      <span className="eyebrow">{label}</span>
      <span className="readout font-medium tabular-nums">{value}</span>
    </div>

    {fill !== null && (
      <span aria-hidden className="mt-1.5 block h-[6px] w-full overflow-hidden rounded-full bg-rule">
        <span
          // A floor of 2%, so a store holding something never renders as an empty track — the
          // difference between "nothing here" and "barely anything here" is the whole reading.
          style={{ width: `${Math.max(2, Math.min(100, fill * 100))}%` }}
          className={clsx(
            'block h-full rounded-full transition-[width] duration-500 ease-out',
            tone === 'alert' && 'bg-alert',
            tone === 'grounded' && 'bg-grounded',
            tone === 'primary' && 'bg-primary'
          )}
        />
      </span>
    )}

    {detail && <p className="readout mt-1.5 text-pretty leading-[1.45] text-fg-subtle">{detail}</p>}
  </div>
)

export const EmptyState = ({ title, body, action }: { title: string; body: string; action?: ReactNode }) => (
  <div className="mx-auto flex max-w-sm flex-col items-center gap-3 py-14 text-center">
    <h3 className="text-[15px]">{title}</h3>
    <p className="text-pretty text-[13.5px] leading-relaxed text-fg-muted">{body}</p>
    {action}
  </div>
)

/**
 * The one spinner.
 *
 * `aria-hidden`, always: it is decoration, and every call site pairs it with `sr-only` text
 * or a `role="status"` region that says what is being waited for. A spinner that announces
 * itself as "img" tells a screen-reader user nothing at all.
 */
export const Spinner = ({ className, size = 14 }: { className?: string; size?: number }) => (
  <span
    aria-hidden
    style={{ width: size, height: size }}
    className={clsx(
      'inline-block animate-spin rounded-full border-[1.5px] border-current border-r-transparent',
      className
    )}
  />
)
